import type { Translations } from "../types";

export const fr: Translations = {
    nav: {
        story: "Histoire",
        trailer: "Bande-annonce",
        pillars: "Piliers",
        media: "Médias",
        roadmap: "Feuille de route",
        help: "Aide",
    },
    cta: {
        wishlist: "Ajouter à la liste de souhaits Steam",
        newsletter: "S'inscrire à la newsletter",
        exploreMission: "Découvrir la mission",
    },
    hero: {
        badge: "Pré-alpha // Développement actif",
        subheadline:
            "Personne ne viendra vous sauver. Il ne reste qu'une issue: Mars, la planète qui a brisé toutes les expéditions parties avant vous. Vous êtes l'Expédition 744.",
        scrollLabel: "Découvrir l'histoire",
        watchTrailer: "Regarder la bande-annonce",
        newsletterLabel: "Suivre le développement",
        newsletterReassurance: "Pas de spam · Désinscription en un clic",
    },
    story: {
        chapter1: {
            eyebrow: "Chapitre I // L'effondrement",
            headline: "On nous avait prévenus. Nous n'avons pas écouté.",
            paragraphs: [
                "Dès 2099, les signaux d'alerte s'accumulaient depuis des décennies. Dérèglement climatique, effondrement de la biosphère, pénuries en cascade. Les gouvernements temporisaient. Les grandes entreprises détournaient le regard. Mais les données menaient toujours à la même conclusion.",
                "En 2145, la Terre avait franchi un point de non-retour. L'agriculture s'effondrait sur plusieurs continents. Des systèmes océaniques entiers se déréglaient. Des régions entières devenaient inhabitables. La planète qui nous avait portés pendant des millénaires n'était plus capable de nous faire vivre.",
                "Ce qui n'était au départ qu'un programme de recherche a été relancé comme un projet de survie. Son nom: l'Initiative GreenMars. Son objectif n'était plus d'étudier Mars, mais de la rendre habitable avant qu'il ne soit trop tard.",
            ],
        },
        chapter2: {
            eyebrow: "Chapitre II // Les expéditions",
            headline: "743 missions avant vous. Presque aucune n'a survécu.",
            paragraphs: [
                "Au fil des décennies suivantes, l'humanité a engagé sur Mars ce qu'il lui restait de puissance industrielle. Les premières expéditions y ont bâti des avant-postes miniers, des laboratoires automatisés et les premières infrastructures d'atterrissage. Chaque mission avançait à partir de ce que la précédente avait laissé derrière elle.",
                "Le constat, pourtant, est accablant. Le contact a été perdu avec presque toutes les expéditions habitées. Certaines bases ont tenu quelques mois avant de se taire. D'autres ont disparu pendant la descente.",
                "Un seul équipage est revenu, en état d'urgence. Leur vaisseau était ravagé, et un unique extrait de leur débriefing a été rendu public:",
            ],
            quote: "« Nous sommes condamnés. N'envoyez plus jamais personne sur cette planète maudite. »",
            closing:
                "Malgré tout, Mars reste la dernière vraie chance de l'humanité. Et cette fois, c'est à vous d'y aller.",
        },
        chapter3: {
            eyebrow: "Chapitre III // Votre mission",
            headline: "Vous ne partez pas explorer Mars. Vous allez la refaçonner.",
            description:
                "L'Expédition 744 n'est pas une mission d'exploration. C'est une opération de transformation planétaire à grande échelle. Vous arrivez avec des ingénieurs, des scientifiques, des opérateurs de terrain, des systèmes de construction automatisés et le dispositif d'infrastructure le plus ambitieux jamais assemblé pour la terraformation d'un autre monde.",
            objectives: [
                "Établir une présence industrielle permanente à la surface",
                "Déployer l'appareil productif nécessaire à toute la mission",
                "Modifier l'atmosphère, la température et la protection magnétique d'une planète entière",
                "Introduire des systèmes biologiques et amorcer la transformation écologique de Mars",
            ],
        },
    },
    pillars: {
        eyebrow: "Les trois piliers",
        headline: "Une mission. Trois fronts.",
        videoCaption: "La transformation écologique à l'œuvre",
        videoDescription: "Des composés conçus pour cela remodèlent peu à peu la surface martienne.",
        description:
            "Mars ne se transformera pas à l'aide d'un seul système. La mission repose sur trois piliers étroitement liés. Pris séparément, chacun reste fragile. Ensemble, ils peuvent ramener un monde mort vers le vivant.",
        items: [
            {
                title: "Expansion industrielle",
                subtitle: "Mettre la machine en marche",
                description:
                    "Production d'énergie, extraction minière, fabrication, logistique, systèmes de lancement. Sur Mars, chaque machine doit être conçue, installée et entretenue par vos soins. Sans base industrielle, rien d'autre ne tient.",
            },
            {
                title: "Ingénierie planétaire",
                subtitle: "Redessiner le monde",
                description:
                    "Déployez des mégastructures capables d'agir à l'échelle d'une planète. Des moteurs orbitaux pour infléchir la trajectoire de Mars autour du Soleil. Des réseaux magnétiques pour protéger l'atmosphère de l'érosion solaire.",
            },
            {
                title: "Transformation écologique",
                subtitle: "Faire naître la vie sur Mars",
                description:
                    "Traitez l'atmosphère, libérez l'eau, préparez les sols, introduisez la vie microbienne et étendez la végétation. La terraformation n'est pas un interrupteur. C'est une réaction en chaîne.",
            },
        ],
    },
    threats: {
        eyebrow: "L'adversité",
        headline: "Mars ne veut pas de vous.",
        description:
            "La planète oppose une résistance à tous les niveaux. Froid extrême. Radiations mortelles. Tempêtes de poussière. Instabilité atmosphérique. Et autre chose encore. Une forme de vie inconnue s'est adaptée aux conditions martiennes d'une manière que personne n'avait anticipée. Elle défend son territoire. Elle encaisse. Et elle est hostile.",
    },
    trailer: {
        eyebrow: "En mouvement",
        headline: "Voir la bande-annonce",
    },
    media: {
        eyebrow: "Depuis la surface",
        headline: "Transmissions de pré-alpha",
        description:
            "Des images brutes du développement, prises au plus près du terrain. Chaque capture est réelle, chaque système est en train d'être construit. L'Expédition 744 prend forme.",
        captions: [
            "Premier avant-poste industriel établi",
            "La surface martienne s'étire à perte de vue",
            "Les opérations d'extraction commencent",
            "La base s'étend malgré un environnement hostile",
            "Surveillance des paramètres planétaires",
            "Les chaînes de fabrication se mettent en route",
            "Les premiers signes du changement",
        ],
    },
    roadmap: {
        eyebrow: "La suite de la mission",
        headline: "Là où nous en sommes, et là où nous allons",
        description:
            "Le développement avance par jalons, pas au rythme de dates arbitraires. Nous livrons quand c'est prêt, et nous montrons clairement où nous en sommes.",
        status: {
            completed: "Terminé",
            current: "En cours",
            upcoming: "Prochain jalon",
            future: "Prévu",
        },
        milestones: [
            {
                phase: "Fondations",
                title: "Systèmes de base et prototype",
                description:
                    "Mise en place des systèmes de gameplay essentiels et du socle technique du projet.",
                items: [
                    "Système principal de ressources",
                    "Rendu du terrain et de l'environnement",
                    "Systèmes de cavernes et de souterrains",
                    "Premier prototype de boucle de gameplay",
                ],
            },
            {
                phase: "Priorité actuelle",
                title: "Systèmes industriels et planétaires",
                description:
                    "Développement du pilier industriel et des premières mécaniques d'ingénierie planétaire.",
                items: [
                    "Approfondissement de la chaîne de fabrication",
                    "Couche de simulation atmosphérique",
                    "Premières mécaniques de terraformation planétaire",
                    "Systèmes océaniques et aquatiques planétaires",
                ],
            },
            {
                phase: "Prochain jalon",
                title: "Cap sur la démo publique",
                description:
                    "Une tranche verticale jouable, pensée pour montrer clairement la vision du jeu et faire vivre la mission aux joueurs.",
                items: [
                    "Scénario de démo peaufiné",
                    "Parcours d'introduction",
                    "Optimisation des performances",
                    "Première expérience PvE",
                ],
            },
            {
                phase: "Direction prévue",
                title: "Vers l'accès anticipé",
                description:
                    "Étoffer les trois piliers, approfondir les systèmes et ouvrir une progression de jeu sur la durée.",
                items: [
                    "Pilier de transformation écologique complet",
                    "Ingénierie planétaire avancée",
                    "Machines de terraformation planétaire avancées",
                    "Sortie en accès anticipé sur Steam",
                    "Première version pleinement jouable de la mission",
                ],
            },
            {
                phase: "Vision à long terme",
                title: "La mission dans son intégralité",
                description:
                    "L'expérience complète d'Expédition 744: une simulation profonde et interconnectée de transformation planétaire.",
                items: [
                    "Intégration complète des trois piliers",
                    "Cycle complet de terraformation",
                    "Évolution nourrie par la communauté",
                ],
            },
        ],
    },
    wishlist: {
        eyebrow: "Rejoindre la mission",
        headline: "L'expédition a besoin de vous",
        description:
            "L'Expédition 744 se construit en ce moment même. L'ajouter à votre liste de souhaits Steam est le geste le plus utile que vous puissiez faire aujourd'hui. C'est un signal clair: cette mission compte.",
        reasons: [
            "Être prévenu dès que la démo sera disponible",
            "Renforcer directement la visibilité du jeu sur Steam",
            "Soutenir un studio indépendant et aider le projet à durer",
        ],
        cta: "Ajouter à la liste de souhaits Steam",
    },
    newsletter: {
        eyebrow: "Rester en contact",
        headline: "Recevoir les transmissions",
        description:
            "Inscrivez-vous pour suivre le développement depuis la surface. Pas de spam, seulement de vraies avancées et des jalons importants.",
        placeholder: "votre@email.com",
        cta: "S'inscrire",
        subscribing: "Inscription...",
        successMessage: "Vérifiez votre boîte mail: le lien de confirmation est en route.",
        successDetail:
            "Cliquez sur le lien contenu dans l'email pour valider votre inscription. Il expirera dans 24 heures.",
        spamNote:
            "Vous ne voyez rien arriver ? Pensez à vérifier vos spams ou votre courrier indésirable.",
        confirmedMessage: "Inscription confirmée. Vous recevrez les prochaines transmissions.",
        confirmedDetail:
            "Votre adresse est bien enregistrée. Nous vous écrirons quand il y aura réellement quelque chose à annoncer.",
        unsubscribedMessage: "Désinscription prise en compte.",
        unsubscribedDetail:
            "Cette adresse email ne recevra plus les mises à jour de la newsletter. Vous pourrez vous réinscrire à tout moment.",
        reassurance: "Aucun spam. Désinscription à tout moment. Uniquement des nouvelles qui comptent.",
        emailError: "Veuillez saisir une adresse email valide.",
        genericError: "Impossible de vous inscrire pour le moment. Veuillez réessayer un peu plus tard.",
        inlineButton: "M'avertir",
        inlineSuccess: "Consultez votre boîte mail pour confirmer votre inscription.",
        inlineSuccessSpam: "Toujours rien ? Vérifiez le dossier spam.",
    },
    footer: {
        closingLine: "Expédition 744. Un projet signé 744 Interactive, mené avec conviction.",
        navigation: "Navigation",
        connect: "Suivre le projet",
        legal: "Légal",
        legalNotice: "Mentions légales",
        privacyPolicy: "Politique de confidentialité",
        cookies: "Cookies",
        copyright: `© ${new Date().getFullYear()} 744 Interactive. Tous droits réservés.`,
    },
    legal: {
        pageTitle: "Mentions légales",
        lastUpdated: "Dernière mise à jour : avril 2026",
        backHome: "Retour à l'accueil",
        legalNotice: {
            title: "Mentions légales",
            rows: [
                { label: "Editeur", value: "744 Interactive" },
                {
                    label: "Directeur de la publication",
                    value: "Anonyme. Coordonnées communiquées à l'hébergeur, disponibles pour les autorités judiciaires sur réquisition (art. 6 III LCEN).",
                },
                { label: "Contact", value: "contact@744interactive.com" },
                { label: "Hébergement", value: "LWS (Label Web Solutions), lws.fr, France" },
            ],
        },
        privacy: {
            title: "Politique de confidentialité",
            sections: [
                {
                    title: "Données collectées",
                    body: [
                        "Newsletter : adresse email, date d'inscription, statut de confirmation.",
                        "Tickets de support : adresse email, catégorie, objet, description, et en option la version du jeu et la plateforme.",
                        "Statistiques : métriques anonymes et agrégées via Vercel Analytics. Aucun cookie, aucune donnée personnelle.",
                    ],
                },
                {
                    title: "Finalités et bases légales",
                    body: [
                        "Newsletter : envoi de mises à jour. Base légale : consentement (RGPD art. 6-1-a).",
                        "Support : traitement des demandes. Base légale : consentement et intérêt légitime (RGPD art. 6-1-a et f).",
                        "Statistiques : mesure des performances du site. Base légale : intérêt légitime (RGPD art. 6-1-f).",
                    ],
                },
                {
                    title: "Durée de conservation",
                    body: [
                        "Données newsletter supprimées sur désinscription.",
                        "Tickets de support conservés 3 ans.",
                    ],
                },
                {
                    title: "Vos droits",
                    body: [
                        "Vous pouvez accéder à vos données, les corriger, les supprimer ou en limiter le traitement. Ecrivez-nous à contact@744interactive.com, nous répondrons sous 30 jours.",
                        "Vous pouvez aussi déposer une réclamation auprès de la CNIL (cnil.fr).",
                    ],
                },
            ],
        },
        cookies: {
            title: "Cookies",
            body: [
                "Ce site utilise un minimum de cookies et aucun tracking.",
                "Vercel Analytics fonctionne sans cookie et ne stocke aucune donnée sur votre appareil.",
                "Vous pouvez supprimer les cookies depuis les paramètres de votre navigateur.",
            ],
        },
    },
    help: {
        eyebrow: "Aide & support",
        title: "Comment pouvons-nous vous aider ?",
        description:
            "Consultez les questions les plus fréquentes ci-dessous, ou envoyez-nous un ticket si vous avez besoin d'une aide directe. Nous lisons tout.",
    },
    faq: {
        title: "Questions fréquentes",
        items: [
            {
                category: "Accès au jeu",
                question: "Quand pourrai-je jouer à Expédition 744 ?",
                answer:
                    "Le jeu est actuellement en pré-alpha. Notre prochain grand jalon est une démo publique. Ajoutez-le à votre liste de souhaits Steam pour être averti dès qu'elle sera disponible.",
            },
            {
                category: "Accès au jeu",
                question: "Une démo gratuite est-elle prévue ?",
                answer:
                    "Oui. Une démo gratuite est prévue comme première sortie publique. Elle donnera un aperçu concret de l'expérience de jeu avant l'accès anticipé.",
            },
            {
                category: "Technique",
                question: "Quelles seront les configurations requises ?",
                answer:
                    "Les configurations requises seront communiquées à l'approche de la sortie de la démo. Nous optimisons activement le jeu pour qu'il tourne sur une large gamme de machines.",
            },
            {
                category: "Technique",
                question: "Le jeu plante ou tourne mal. Que faire ?",
                answer:
                    "Envoyez-nous un ticket via le formulaire ci-dessous avec votre configuration, la version du jeu et une description du problème. Les logs et les captures d'écran nous aident à aller plus vite dans le diagnostic.",
            },
            {
                category: "Gameplay",
                question: "Quel genre de jeu est Expédition 744 ?",
                answer:
                    "C'est une simulation de colonie de science-fiction ambitieuse, centrée sur la transformation planétaire, qui mêle expansion industrielle, ingénierie planétaire et terraformation écologique sur Mars.",
            },
            {
                category: "Gameplay",
                question: "Y a-t-il des problèmes de sauvegarde à connaître ?",
                answer:
                    "Pendant la pré-alpha et la phase de démo, le format des sauvegardes peut évoluer d'une version à l'autre. Nous signalerons toujours clairement les changements incompatibles.",
            },
            {
                category: "Retours",
                question: "Comment partager un retour ou signaler un bug ?",
                answer:
                    "Utilisez le formulaire de support ci-dessous. Choisissez la catégorie Rapport de bug ou Retour. Nous lisons chaque message.",
            },
        ],
    },
    support: {
        title: "Envoyer un ticket",
        subtitle: "Décrivez votre problème et nous reviendrons vers vous dès que possible.",
        email: "Email",
        category: "Catégorie",
        categoryPlaceholder: "Choisissez une catégorie",
        subject: "Sujet",
        subjectPlaceholder: "Résumez brièvement votre problème",
        description: "Description",
        descriptionPlaceholder:
            "Décrivez le problème en détail: étapes pour le reproduire, comportement attendu, comportement observé, etc.",
        gameVersion: "Version du jeu",
        gameVersionOptional: "(facultatif)",
        gameVersionPlaceholder: "ex. 0.1.0-alpha",
        platform: "Plateforme / OS",
        platformOptional: "(facultatif)",
        platformPlaceholder: "ex. Windows 11, Steam Deck",
        submit: "Envoyer le ticket",
        submitting: "Envoi...",
        successTitle: "Ticket envoyé",
        successDetail:
            "Merci pour votre message. Nous avons bien reçu votre ticket et nous vous répondrons par email dès que possible.",
        successAnother: "Envoyer un autre ticket",
        genericError:
            "Impossible d'envoyer votre ticket pour le moment. Veuillez réessayer un peu plus tard.",
        categories: ["Rapport de bug", "Problème technique", "Accès à la démo", "Retour"],
    },
};
